import { useState, useCallback } from 'react'
import { useLoadedManta } from '@happy-ops-org/manta-react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { isRight } from 'fp-ts/Either'
import { PathReporter } from 'io-ts/PathReporter'
import * as t from 'io-ts'
import { orcaQueryImportFileCodec } from './codecs.js'
import { orcaQueryDef } from './manta.js'
import type { OrcaQueryDefinition } from './types.js'

type ImportData = t.TypeOf<typeof orcaQueryImportFileCodec>

type ValidationState =
  | { status: 'idle' }
  | { status: 'error'; message: string }
  | { status: 'invalid'; errors: string[] }
  | { status: 'valid'; data: ImportData; filename: string }
  | { status: 'importing' }
  | { status: 'done'; created: number; updated: number }

const EXAMPLE_JSON = JSON.stringify(
  {
    'Ofen Linie 1': {
      description: 'Temperatur- und Statuswerte der Brennlinie 1.',
      tags: ['line', 'line:1'],
      fields: {
        temperature: {
          type: 'number',
          description: 'Ofentemperatur',
          unit: '°C',
        },
        'sensor.status': { type: 'string', description: 'Sensorzustand' },
      },
      observedTags: {
        'shift:a': { description: 'Frühschicht' },
        'sensor:temp': { description: 'Temperatursensor' },
      },
    },
  },
  null,
  2,
)

/**
 * Import page for saved Orca queries. Reads a JSON file of { [name]: query } —
 * the same shape the export produces — validates it, and upserts each query by
 * name (updates an existing query with that name, otherwise creates it). Only
 * name/description/tags/fields/observedTags are imported; resolution state is
 * left to the server (re-resolve via "Schema laden/aktualisieren").
 */
export const OrcaQueryImportPage = () => {
  const manta = useLoadedManta()
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
  })
  const [dragging, setDragging] = useState(false)

  const processFile = async (file: File) => {
    setValidation({ status: 'idle' })
    try {
      const text = await file.text()
      const json = JSON.parse(text)
      const result = orcaQueryImportFileCodec.decode(json)
      if (isRight(result)) {
        setValidation({
          status: 'valid',
          data: result.right,
          filename: file.name,
        })
      } else {
        setValidation({
          status: 'invalid',
          errors: PathReporter.report(result),
        })
      }
    } catch (err) {
      setValidation({
        status: 'error',
        message: `Ungültiges JSON: ${String(err)}`,
      })
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file?.name.endsWith('.json')) processFile(file)
  }, [])

  const handleImport = async () => {
    if (validation.status !== 'valid') return
    const { data } = validation
    setValidation({ status: 'importing' })

    // Upsert by name: queries have no lid, so match the body `name` field.
    const existing = await manta.query(orcaQueryDef, {})
    const idByName = new Map<string, number>()
    for (const q of existing) {
      if (q.data?.name) idByName.set(q.data.name, q.id)
    }

    let created = 0
    let updated = 0
    for (const [name, def] of Object.entries(data)) {
      const payload: OrcaQueryDefinition = {
        name,
        description: def.description,
        tags: def.tags,
        fields: def.fields,
        observedTags: def.observedTags,
      }
      const id = idByName.get(name)
      if (id !== undefined) {
        await manta.cmd(orcaQueryDef, id, async (state) => {
          state.data = payload
          return true
        })
        updated += 1
      } else {
        await manta.create(orcaQueryDef, payload)
        created += 1
      }
    }
    setValidation({ status: 'done', created, updated })
  }

  return (
    <Box p={4} maxWidth={800} mx="auto">
      <Typography variant="h5" fontWeight={700} mb={1}>
        Import
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Lade eine JSON-Datei hoch, um gespeicherte Abfragen zu importieren
        (gleiches Format wie der Export). Abfragen werden über ihren Namen
        abgeglichen: ein vorhandener Name wird aktualisiert, sonst neu angelegt.
      </Typography>

      {/* Drop Zone */}
      <Paper
        variant="outlined"
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          p: 5,
          mb: 4,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'primary.50' : 'grey.50',
          transition: 'all 0.15s',
          cursor: 'pointer',
        }}
        onClick={() =>
          document.getElementById('orca-query-import-input')?.click()
        }
      >
        <UploadFileIcon
          sx={{
            fontSize: 48,
            color: dragging ? 'primary.main' : 'text.disabled',
            mb: 1,
          }}
        />
        <Typography
          fontWeight={600}
          color={dragging ? 'primary.main' : 'text.secondary'}
        >
          JSON-Datei hier ablegen oder klicken zum Auswählen
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Nur .json Dateien
        </Typography>
        <input
          id="orca-query-import-input"
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </Paper>

      {validation.status === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validation.message}
        </Alert>
      )}

      {validation.status === 'invalid' && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<ErrorIcon />}>
          <Typography fontWeight={600} mb={1}>
            Schema-Fehler
          </Typography>
          <Stack spacing={0.5}>
            {validation.errors.map((e, i) => (
              <Typography
                key={i}
                variant="body2"
                fontFamily="monospace"
                fontSize={12}
              >
                {e}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {validation.status === 'valid' && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography fontWeight={600}>
                {Object.keys(validation.data).length} Abfragen validiert —{' '}
                {validation.filename}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                {Object.keys(validation.data).map((name) => (
                  <Chip key={name} label={name} size="small" />
                ))}
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleImport}
              sx={{ ml: 2, flexShrink: 0 }}
            >
              Importieren
            </Button>
          </Box>
        </Alert>
      )}

      {validation.status === 'importing' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Import läuft…
        </Alert>
      )}

      {validation.status === 'done' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {validation.created} neu angelegt, {validation.updated} aktualisiert.
        </Alert>
      )}

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h6" fontWeight={700} mb={2}>
        Erwartetes Format
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Ein JSON-Objekt, dessen Keys die Abfrage-Namen sind.
      </Typography>

      <Table size="small" sx={{ mb: 4 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Feld</strong>
            </TableCell>
            <TableCell>
              <strong>Typ</strong>
            </TableCell>
            <TableCell>
              <strong>Pflicht</strong>
            </TableCell>
            <TableCell>
              <strong>Beschreibung</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            ['description', 'string', '✓', 'Beschreibung der Abfrage'],
            ['tags', 'string[]', '✓', 'Das UND-verknüpfte Tag-Set'],
            ['fields', 'object', '✓', 'Feld-Annotationen (Key = Feldpfad)'],
            [
              'fields.[pfad].type',
              'string',
              '✓',
              'Generierter Typ (z.B. "number")',
            ],
            [
              'fields.[pfad].description',
              'string',
              '✓',
              'Bedeutung des Feldes',
            ],
            ['fields.[pfad].unit', 'string', '–', 'Einheit, z.B. "°C"'],
            [
              'fields.[pfad].relatesTo',
              'string',
              '–',
              'Referenz, z.B. "machines.id"',
            ],
            ['observedTags', 'object', '✓', 'Beobachtete Tags (Key = Tag)'],
            [
              'observedTags.[tag].description',
              'string',
              '✓',
              'Bedeutung des Tags',
            ],
          ].map(([field, type, required, desc]) => (
            <TableRow key={field}>
              <TableCell>
                <code>{field}</code>
              </TableCell>
              <TableCell>
                <code>{type}</code>
              </TableCell>
              <TableCell>{required}</TableCell>
              <TableCell>{desc}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Beispiel
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}
      >
        <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
          {EXAMPLE_JSON}
        </pre>
      </Paper>
    </Box>
  )
}
